import { useMemo } from 'react'
import { Pressable, ScrollView, Text, View } from 'react-native'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { Icon, symbolName } from '@/src/components/ui/Icon'
import { Spinner } from '@/src/components/ui/Spinner'
import { useAppSessionDefinitionIds } from '@/src/hooks/useAppSessionDefinitionIds'
import { useBackendSessionsQuery } from '@/src/queries/session-queries'
import { colors } from '@/src/theme'
import { useTranslation } from 'react-i18next'

import { VoiceAssessmentHistoryAccordion } from '../components/voice-assessment/VoiceAssessmentHistoryAccordion'
import {
  STATISTICS_CONTENT_PADDING_BOTTOM,
  STATISTICS_CONTENT_PADDING_X,
} from '../constants/statistics.styles'
import { selectVoiceAssessmentSessions } from '../utils/select-voice-assessment-sessions'

/** When there is no stack entry (e.g. web refresh), `router.back()` is a no-op — return to Statistics. */
const VOICE_ASSESSMENT_HISTORY_BACK_FALLBACK =
  '/(protected)/(account)/(tabs)/statistics' as const

export default function VoiceAssessmentHistoryScreen() {
  const { back, canGoBack, replace } = useRouter()
  const insets = useSafeAreaInsets()
  const { t } = useTranslation(['statistics', 'common'])
  const sessionDefinitionIds = useAppSessionDefinitionIds()

  const {
    data: sessions = [],
    isError,
    isPending,
    refetch,
  } = useBackendSessionsQuery()
  const entries = useMemo(
    () => selectVoiceAssessmentSessions(sessions, sessionDefinitionIds),
    [sessionDefinitionIds, sessions],
  )

  const handleGoBack = () => {
    if (canGoBack()) {
      back()
      return
    }
    replace(VOICE_ASSESSMENT_HISTORY_BACK_FALLBACK)
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}
      edges={['top', 'left', 'right']}
    >
      <Head>
        <title>{t('statistics:voice_assessment_details_screen_title')}</title>
      </Head>
      <View
        className="flex-row items-center pb-3 pt-2"
        style={{ paddingHorizontal: STATISTICS_CONTENT_PADDING_X }}
      >
        <Pressable
          onPress={handleGoBack}
          hitSlop={12}
          className="mr-3 p-1 active:opacity-70"
          accessibilityRole="button"
          accessibilityLabel={t('common:back')}
        >
          <Icon
            name={symbolName('chevron.left', 'arrow_back')}
            size={24}
            color="ink"
          />
        </Pressable>
        <Text className="text-lg font-semibold text-ink flex-1 pr-2">
          {t('statistics:voice_assessment_details_screen_title')}
        </Text>
      </View>

      {isPending ? (
        <View className="justify-center items-center py-16" style={{ flex: 1 }}>
          <Spinner color={colors.primary} />
        </View>
      ) : isError ? (
        <View
          className="justify-center px-6"
          style={{
            flex: 1,
            paddingHorizontal: STATISTICS_CONTENT_PADDING_X,
          }}
        >
          <Text className="text-base font-semibold text-ink text-center mb-2">
            {t('statistics:voice_assessment_error_title')}
          </Text>
          <Text className="text-sm text-muted text-center mb-4">
            {t('statistics:voice_assessment_error_message')}
          </Text>
          <Pressable
            onPress={() => {
              void refetch()
            }}
            className="self-center rounded-lg px-4 py-3 active:opacity-90"
            style={{ backgroundColor: colors.corePrimary }}
          >
            <Text className="text-sm font-semibold text-ink">
              {t('common:error_try_again')}
            </Text>
          </Pressable>
        </View>
      ) : entries.length === 0 ? (
        <View
          className="justify-center px-6"
          style={{
            flex: 1,
            paddingHorizontal: STATISTICS_CONTENT_PADDING_X,
          }}
        >
          <Text className="text-base font-semibold text-ink text-center mb-2">
            {t('statistics:voice_assessment_empty_title')}
          </Text>
          <Text className="text-sm text-muted text-center">
            {t('statistics:voice_assessment_empty_subtitle')}
          </Text>
        </View>
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentInset={{
            bottom: STATISTICS_CONTENT_PADDING_BOTTOM + insets.bottom,
          }}
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: STATISTICS_CONTENT_PADDING_X,
            paddingTop: 16,
          }}
          showsVerticalScrollIndicator={false}
        >
          <VoiceAssessmentHistoryAccordion
            key={entries.map((entry) => entry.id).join('|')}
            entries={entries}
          />
          <View
            style={{
              height: STATISTICS_CONTENT_PADDING_BOTTOM + insets.bottom,
            }}
          />
        </ScrollView>
      )}
    </SafeAreaView>
  )
}
